import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Bell, Settings, LogOut, User, Bot, MessageSquare, Users, Send, Contact } from 'lucide-react';
import { useChatContext } from '../hooks/useChatContext';
import { useAuth } from '../hooks/useAuth';
import ConversationList from './ConversationList';
import ChatArea from './ChatArea';
import MessageInput from './MessageInput';
import CustomerInfo from './CustomerInfo';
import AdminPanel from './AdminPanel';
import BroadcastManager from './BroadcastManager';
import ContactManager from './ContactManager';
import { Conversation } from '../types';
import { toast } from 'react-hot-toast';

const Dashboard: React.FC = () => {
  const { state, selectConversation, updateAgentStatus } = useChatContext();
  const { user, logout } = useAuth();
  const [showCustomerInfo, setShowCustomerInfo] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [agentStatus, setAgentStatus] = useState<'online' | 'offline' | 'busy'>('online');
  const [activeTab, setActiveTab] = useState<'conversations' | 'broadcast' | 'contacts'>('conversations');

  // Memoize stats calculation to prevent re-calculation on every render
  const stats = useMemo(() => ({
    total: state.conversations.length,
    active: state.conversations.filter(c => c.status === 'active').length,
    pending: state.conversations.filter(c => c.status === 'pending').length,
    aiHandled: state.conversations.filter(c => c.status === 'ai-handled').length,
  }), [state.conversations]);

  // Memoize the status color function
  const getStatusColor = useCallback((status: string) => {
    switch (status) {
      case 'online': return 'bg-green-500';
      case 'busy': return 'bg-yellow-500';
      case 'offline': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  }, []);

  // Memoize callback functions to prevent re-renders
  const handleSelectConversation = useCallback((conversation: Conversation) => {
    selectConversation(conversation.id);
  }, [selectConversation]);

  const handleStatusChange = useCallback((newStatus: 'online' | 'offline' | 'busy') => {
    setAgentStatus(newStatus);
    updateAgentStatus(newStatus);
  }, [updateAgentStatus]);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      // No need to redirect, auth context will handle showing login page
    } catch (error) {
      toast.error('Logout failed');
    }
  }, [logout]);

  const handleSettings = useCallback(() => {
    setShowAdminPanel(true);
  }, []);

  const handleNotifications = useCallback(() => {
    // Show notifications panel
    toast('Notifications panel');
  }, []);

  // Remove problematic useEffect that causes re-renders
  useEffect(() => {
    // Only update agent status on mount, not on every agentStatus change
    updateAgentStatus('online');
  }, [updateAgentStatus]);

  return (
    <div className="h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-900">Omnichannel Chat</h1>
            
            {/* Stats */}
            <div className="flex gap-4 ml-8">
              <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-lg">
                <MessageSquare className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">{stats.total} Total</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1 bg-green-50 rounded-lg">
                <Users className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-900">{stats.active} Active</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1 bg-yellow-50 rounded-lg">
                <Bell className="w-4 h-4 text-yellow-600" />
                <span className="text-sm font-medium text-yellow-900">{stats.pending} Pending</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1 bg-purple-50 rounded-lg">
                <Bot className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-purple-900">{stats.aiHandled} AI</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Notifications */}
            <button 
              onClick={handleNotifications}
              className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            >
              <Bell className="w-5 h-5" />
              {state.aiHandoffs.length > 0 && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
              )}
            </button>

            {/* Agent Status */}
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
              <div className={`w-2 h-2 rounded-full ${getStatusColor(agentStatus)}`}></div>
              <select
                value={agentStatus}
                onChange={(e) => handleStatusChange(e.target.value as any)}
                className="bg-transparent border-none text-sm font-medium text-gray-700 focus:ring-0"
              >
                <option value="online">Online</option>
                <option value="busy">Busy</option>
                <option value="offline">Offline</option>
              </select>
            </div>

            {/* User Menu */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700">
                {user?.name || 'Agent'}
              </span>
            </div>

            {/* Settings */}
            <button 
              onClick={handleSettings}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              title="Settings & Admin"
            >
              <Settings className="w-5 h-5" />
            </button>

            {/* Logout */}
            <button 
              onClick={handleLogout}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <nav className="flex space-x-8 px-6">
          <button
            onClick={() => setActiveTab('conversations')}
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'conversations'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <MessageSquare size={16} className="inline mr-1" />
            Conversations
          </button>
          <button
            onClick={() => setActiveTab('broadcast')}
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'broadcast'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Send size={16} className="inline mr-1" />
            Broadcast
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'contacts'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Contact size={16} className="inline mr-1" />
            Contacts
          </button>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'conversations' && (
          <div className="h-full flex">
            {/* Conversations Sidebar */}
            <ConversationList onSelectConversation={handleSelectConversation} />

            {/* Chat Area */}
            <div className="flex-1 flex flex-col">
              <div className="flex-1 flex">
                <div className="flex-1 flex flex-col">
                  <ChatArea />
                  <MessageInput />
                </div>

                {/* Customer Info Panel */}
                {showCustomerInfo && state.activeConversation && (
                  <CustomerInfo 
                    conversation={state.activeConversation}
                    onClose={() => setShowCustomerInfo(false)}
                  />
                )}
              </div>
            </div>

            {/* Toggle Customer Info */}
            {state.activeConversation && (
              <button
                onClick={() => setShowCustomerInfo(!showCustomerInfo)}
                className="absolute top-32 right-4 p-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                title="Toggle customer information"
              >
                <User className="w-4 h-4 text-gray-600" />
              </button>
            )}
          </div>
        )}

        {activeTab === 'broadcast' && (
          <div className="h-full bg-gray-50">
            <BroadcastManager />
          </div>
        )}

        {activeTab === 'contacts' && (
          <div className="h-full bg-gray-50">
            <ContactManager />
          </div>
        )}
      </div>

      {/* AI Handoff Notifications */}
      {state.aiHandoffs.length > 0 && (
        <div className="fixed bottom-4 right-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 shadow-lg max-w-sm">
          <div className="flex items-center gap-2 mb-2">
            <Bot className="w-5 h-5 text-yellow-600" />
            <h3 className="font-medium text-yellow-800">AI Handoff Request</h3>
          </div>
          <p className="text-sm text-yellow-700">
            {state.aiHandoffs.length} conversation(s) need human attention
          </p>
          <button className="mt-2 text-sm text-yellow-600 hover:text-yellow-800 font-medium">
            View Details →
          </button>
        </div>
      )}

      {/* Admin Panel Modal */}
      {showAdminPanel && (
        <AdminPanel onClose={() => setShowAdminPanel(false)} />
      )}
    </div>
  );
};

export default Dashboard;
